// parseEpg.js
// Parses an XMLTV document (as returned by the backend `/device/:deviceName/
// :password/epg` endpoint) into a plain, timezone-deterministic data model the
// EPG page can render. Times are stored as absolute "wall-clock" minutes
// relative to the feed (computed from the raw components via Date.UTC), so the
// schedule is shown exactly as the broadcaster wrote it, independent of the
// viewer's local timezone.

export const MINUTES_PER_DAY = 24 * 60;

/**
 * Convert an XMLTV datetime string such as `20260914203000 +0000` into the
 * absolute feed wall-clock minutes and the day key it belongs to. The timezone
 * offset is intentionally ignored so the schedule renders exactly as written.
 *
 * @returns {{abs: number, dayKey: string} | null}
 */
export function parseXmltvTime(raw) {
    if (typeof raw !== 'string') {
        return null;
    }
    const match = raw
        .trim()
        .match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
    if (!match) {
        return null;
    }

    const [, year, month, day, hour, minute, second] = match;
    const abs =
        Date.UTC(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10),
            parseInt(hour, 10),
            parseInt(minute, 10),
            parseInt(second, 10),
        ) / 60000;
    return {
        abs,
        dayKey: `${year}-${month}-${day}`,
        hour: parseInt(hour, 10),
        minute: parseInt(minute, 10),
    };
}

/**
 * Parse an XMLTV document into `{ channels, programmes }`.
 *
 * @param {string} xmlText - Raw XMLTV document.
 * @returns {{channels: Array<{id: string, name: string}>,
 *            programmes: Array<{channelId: string, startAbs: number,
 *            stopAbs: number, startDay: string, title: string,
 *            description: string}>}}
 */
export function parseXmltv(xmlText) {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const channels = [];
    const programmes = [];

    doc.querySelectorAll('channel').forEach((element) => {
        const id = element.getAttribute('id');
        if (!id) {
            return;
        }
        const nameEl = element.querySelector('display-name');
        const name = nameEl ? nameEl.textContent.trim() : '';
        channels.push({ id, name: name || id });
    });

    doc.querySelectorAll('programme').forEach((element) => {
        const start = parseXmltvTime(element.getAttribute('start'));
        const stop = parseXmltvTime(element.getAttribute('stop'));
        const titleEl = element.querySelector('title');
        const descEl = element.querySelector('desc');
        if (!start || !stop) {
            return;
        }
        programmes.push({
            channelId: element.getAttribute('channel') || '',
            startAbs: start.abs,
            stopAbs: stop.abs,
            startDay: start.dayKey,
            title: titleEl ? titleEl.textContent.trim() : '',
            description: descEl ? descEl.textContent.trim() : '',
        });
    });

    return { channels, programmes };
}
